"use client";

import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from "react";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { User, AuthState, LoginResponse } from "@/types/auth";
import {jwtDecode} from "jwt-decode";
import { setAuthToken } from "@/components/services/apiClient";

interface AuthContextType {
  authState: AuthState;
  login: (credentials: any) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loading: boolean;
  // Expose the Axios instance for use in other components
  axiosInstance: AxiosInstance;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const REFRESH_BUFFER = 90000;

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    accessToken: null,
    loading: true,
    accessToken_expiry: null,
  });
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectHandlerRef = useRef<(() => void) | null>(null);


  const axiosInstance = useMemo(() => axios.create({
    baseURL: process.env.NEXT_PUBLIC_API_URL, 
    withCredentials: true, 
    headers: {
      "Content-Type": "application/json",
      "X-Client-Type": "web",
      "X-Client-App": "juaso",
    },
  }), [])

  const refreshInstance = useMemo(() => axios.create({
    baseURL: process.env.NEXT_PUBLIC_REFRESH_URL, 
    withCredentials: true, 
    headers: {
      "Content-Type": "application/json",
      "X-Client-Type": "web"
    },
  }), [])

  // const axiosInstance2 = axios.create({
  //   baseURL: process.env.NEXT_PUBLIC_REFRESH_URL, 
  //   withCredentials: true, 
  //   headers: {
  //     "Content-Type": "application/json",
  //     "X-Client-Type": "web",
  //     'Authorization': `Bearer ${authState.accessToken}`
  //   },
  // });


  const restoreSession = async () => {
    try {
      const response = await refreshInstance.post(
        "/token/refresh"
      );

      const accessToken = response.data.access_token;

      const decoded: any = jwtDecode(accessToken);

      const expiresAt = decoded.exp * 1000;

      setAuthToken(accessToken);

      setAuthState({
        user: {
          user_id: decoded.sub,
        },
        accessToken,
        accessToken_expiry: expiresAt,
        loading: false,
      });

      scheduleTokenRefresh(expiresAt);
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

// Hook to run when the component mounts
  useEffect(() => {
    restoreSession();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (reconnectHandlerRef.current) {
        window.removeEventListener("online", reconnectHandlerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // This runs AFTER the component has re-rendered with the new state.
    if (authState.accessToken) {
        console.log("State Updated Successfully:");
        console.log("New Access Token:", authState.accessToken);
        console.log("New Expiry Time:", authState.accessToken_expiry);
        console.log("Loading Status:", authState.loading);
        console.log("user_full_name:", authState.user)
    }
  }, [authState.accessToken, authState.loading, authState.accessToken_expiry]);   

  // Function to handle login

  const login = async (
    credentials: any
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await axiosInstance.post(
        "/auth/login",
        credentials
      );

      const accessToken =
        response.data.jwt.access_token;

      const decoded: any = jwtDecode(accessToken);

      const expiresAt = decoded.exp * 1000;

      const user: User = {
        user_id: response.data.user_id,
        user_full_name:
          response.data.user_full_name,
        email: decoded.email,
      };

      setAuthToken(accessToken);

      setAuthState({
        user,
        accessToken,
        accessToken_expiry: expiresAt,
        loading: false,
      });

      scheduleTokenRefresh(expiresAt);

      return { success: true };
    } catch (error: any) {
      console.error("Login failed", error);
      const message =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.response?.data?.error ||
        "Login failed. Please try again.";
      return { success: false, error: message };
    }
  };

  // Function to handle logout
  const logout = async () => {
    try {
      // Stop any scheduled token refresh
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      // Call the backend's logout endpoint to clear the HttpOnly cookie
      await refreshInstance.post(
        "/logout",
        {},
        {
          headers: {
            Authorization: `Bearer ${authState.accessToken}`,
          },
        } 
      );
    } finally {
      // Clear local state regardless of backend success
      setAuthState({ user: null, accessToken: null, loading: false, accessToken_expiry: null });
    }
  };

  // Axios interceptor for handling token expiration
  useEffect(() => {
    const interceptor =
      axiosInstance.interceptors.response.use(
        (response) => response,

        async (error) => {
          const originalRequest = error.config;

          if (
            error.response?.status === 401 &&
            !originalRequest._retry
          ) {
            originalRequest._retry = true;

            console.log(
              "Access token expired. Refreshing..."
            );

            const newToken =
              await refreshAccessToken();

            if (newToken) {
              originalRequest.headers.Authorization =
                `Bearer ${newToken}`;

              return axiosInstance(originalRequest);
            }
          }

          return Promise.reject(error);
        }
      );

    return () => {
      axiosInstance.interceptors.response.eject(
        interceptor
      );
    };
  }, [authState.accessToken]);

  useEffect(() => {
    const requestInterceptor =
      axiosInstance.interceptors.request.use(
        (config) => {
          if (authState.accessToken) {
            config.headers.Authorization =
              `Bearer ${authState.accessToken}`;
          }

          return config;
        }
      );

    return () => {
      axiosInstance.interceptors.request.eject(
        requestInterceptor
      );
    };
  }, [authState.accessToken]);

  

  const refreshAccessToken = async () => {
    try {
      const response = await refreshInstance.post(
        "/token/refresh"
      );

      const newAccessToken =
        response.data.access_token;

      const decoded: any =
        jwtDecode(newAccessToken);

      const expiresAt = decoded.exp * 1000;

      setAuthToken(newAccessToken);

      setAuthState((prev) => ({
        ...prev,
        accessToken: newAccessToken,
        accessToken_expiry: expiresAt,
      }));

      scheduleTokenRefresh(expiresAt);
      return newAccessToken;
    } catch (error: any) {
      // No response means the request never reached the server (offline,
      // DNS failure, timeout, etc.) - this is not a rejected refresh token,
      // so don't log the user out. Just wait for connectivity to come back
      // and retry then.
      if (!error.response) {
        console.warn(
          "Refresh failed due to network error. Will retry when back online."
        );
        waitForReconnectAndRetry();
        return null;
      }

      console.error(
        "Refresh failed, logging out"
      );

      logout();

      return null;
    }
  };

  // Waits for the browser to report connectivity again, then retries the
  // token refresh. Guards against stacking multiple listeners if several
  // requests fail while offline.
  const waitForReconnectAndRetry = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    if (reconnectHandlerRef.current) {
      return;
    }

    const handleOnline = () => {
      window.removeEventListener("online", handleOnline);
      reconnectHandlerRef.current = null;
      refreshAccessToken();
    };

    reconnectHandlerRef.current = handleOnline;
    window.addEventListener("online", handleOnline);
  };


  const scheduleTokenRefresh = (expiresAt: number) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const timeUntilRefresh =
      expiresAt - Date.now() - REFRESH_BUFFER;

    console.log(
      `Next refresh in ${Math.floor(timeUntilRefresh / 1000)} seconds`
    );

    if (timeUntilRefresh <= 0) {
      console.warn("Token already near expiry")
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshAccessToken();
    }, timeUntilRefresh);
  };



  const value = {
    authState,
    login,
    logout,
    loading: authState.loading,
    axiosInstance,
    
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
